import { MessageType, Sender } from '@prisma/client';
import compact from 'lodash/compact';
import uniqBy from 'lodash/uniqBy';
import { z } from 'zod';

import type { Links, Message, PublicLinks } from '@/components/messenger/types';

type SenderKeyValue = {
  [key in Sender]: string;
};
export const getMessageSenderName = ({
  message,
  target,
  aiAgentName,
}: {
  message: Message;
  target: 'USER' | 'AGENT';
  aiAgentName?: string;
}) => {
  if (!message || !message?.sender) return null;

  const sender: SenderKeyValue = {
    [Sender.AI]: aiAgentName || 'AI Agent',
    [Sender.USER]: target === 'AGENT' ? 'User' : 'You',
    [Sender.AGENT]: message.agent?.name || 'Agent',
  };

  return sender[message.sender];
};

export const distanceToRelevanceScore = (distance: number) => {
  const score = 1 - distance;

  return score.toFixed(2);
};

export const cleanAndDeduplicateLinks = (links: Links): PublicLinks => {
  if (!links) return [];

  const cleanLinks = links.map((item: Links[0]) => ({
    id: item?.libraryId,
    link: item.link,
    type: item.type,
    distance: item.distance,
  }));

  return compact(uniqBy(cleanLinks, 'id'));
};

export const cleanAndDeduplicatePublicLinks = (
  links: PublicLinks,
): PublicLinks => {
  if (!links) return [];

  const cleanLinks = links.map((item) => ({
    ...item,
  }));

  return compact(uniqBy(cleanLinks, 'link'));
};

const MD_IMAGE_REGEX = /^!\[.*?\]\(.*?\)/;
export const isMDImageAtTop = (message: string): boolean => {
  try {
    return MD_IMAGE_REGEX.test(message.trim());
  } catch {
    return false;
  }
};

export const getMessageType = (
  message: string,
  type: MessageType,
): MessageType => {
  const hasImageAtTop = isMDImageAtTop(message);

  return hasImageAtTop ? MessageType.IMAGE : type;
};

export const cleanThinkingTags = (text: string): string => {
  // First, normalize spaces around thinking tags
  let normalizedText = text
    .replace(/<think(?:ing)?>/g, ' <thinking>')
    .replace(/<\/think(?:ing)?>/g, '</thinking> ');

  // Remove thinking tags and their content while preserving spaces
  normalizedText = normalizedText.replace(
    /<thinking>[\s\S]*?<\/thinking>/g,
    '',
  );

  // Add spaces between words that are stuck together
  normalizedText = normalizedText
    // Add space before capital letters that are stuck to lowercase letters
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Add space after punctuation marks if missing
    .replace(/([.,!?])([a-zA-Z])/g, '$1 $2')
    // Add space before punctuation marks if missing
    .replace(/([a-zA-Z])([.,!?])/g, '$1 $2')
    // Add space around special characters
    .replace(/([a-zA-Z])([()\[\]{}])/g, '$1 $2')
    .replace(/([()\[\]{}])([a-zA-Z])/g, '$1 $2');

  // Clean up any double spaces and trim
  return normalizedText.replace(/\s+/g, ' ').trim();
};
